import React, { useContext, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import { useCurrentUser } from "../../CurrentUserContext";
import { Plan2PlateDrawer } from "../Plan2PlateDrawer";
import { PlanCoordinateHeader } from "../PlanCoordinateHeader";
import { MealPlannerContext } from "../../contexts";
import {
  JustIntake,
  MealWithIntakes,
  PlanCell,
  PlanCellCoordinatesWithOptionalPerson,
  PlanCellWithIndex,
} from "../../data/plan";
import {
  canBeConsumed,
  findById,
  getMealKeyForMeal,
  isNullIndex,
  mapPlanCellToNutriValues,
  nutriValueSumReducer,
} from "../../utils/planUtils";
import { isDeleted } from "../../utils/editUtils";
import { usePersonsAsync } from "../../queries/queries";
import {
  ensureFoods,
  ensurePurchases,
  getFoodByUnit,
  useMealPurchases,
  usePurchase,
} from "../../queries/caches";
import { useFoodsByIds } from "../../queries/caches";
import { useQueryClient } from "@tanstack/react-query";
import { collectFoodIds, collectFoodUnitIds } from "../../utils/foodIdUtils";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import { useConsumedNutri } from "./intakeLookups";
import { emptyNutriValue } from "../../data/nutriValues";
import { createIntakesFromEntity } from "../../api";
import { IntakeWithEditedFlags } from "../../data/plan";
import { MealSourceIdDto } from "../../api";
import { createPlanCells } from "../../utils/createMeal";
import { cloneCell } from "../intake/consumedIntakeUtils";
import { DishModification } from "../../data/dishModification";
import { DishVersionWarning } from "../dish/DishVersionWarning";
import { loadDishModification } from "../../loaders";
import { ConsumedProductPicker } from "../intake/ConsumedProductPicker";
import { AdditionalIntakeSection } from "../intake/AdditionalIntakeSection";
import { IntakePlannedSection } from "../intake/IntakePlannedSection";
import { IntakeConsumedSection } from "../intake/IntakeConsumedSection";
import {
  ConsumedCell,
  IngredientEdit,
  PlannedBase,
  PlannedEdit,
  buildConsumeOverrides,
  isIngredientCustom,
  planMetaFromIngredient,
} from "../intake/plannedIntakeUtils";
import { PlannedProductPicker } from "../intake/PlannedConsumeCard";
import { Button } from "../ui/Button";
import { LookupSkeleton } from "../ui/LookupSkeleton";
import { useEntityEditDrawers } from "../useEntityEditDrawers";
import { mealInds } from "../../utils/mealIndUtils";

interface IntakeDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  planCellCoordinates: PlanCellCoordinatesWithOptionalPerson;
  focusPlanCell?: PlanCellWithIndex;
}

function consumedPersonId(planCell: PlanCell) {
  return planCell.indicator === "MealWithIntakes"
    ? planCell.meal.personId
    : planCell.personId;
}

function hasActiveConsumption(planCell: PlanCell) {
  return planCell.indicator === "MealWithIntakes"
    ? (planCell.intakes ?? []).some((i) => !isDeleted(i) && (i.amount || 0) > 0)
    : (planCell.amount || 0) > 0;
}

function consumedSig(planCell: PlanCell) {
  return planCell.indicator === "MealWithIntakes"
    ? `${planCell.meal.personId}:${planCell.meal.id}`
    : `${planCell.personId}:intake-${planCell.id}`;
}

/**
 * Der Zustand des Verzehr-Drawers lebt oberhalb der Slot-`QueryBoundary`s in
 * der Shell. So bleibt beim Rendern über einen Footer-Slot alles erhalten.
 */
function useIntakeState(props: IntakeDrawerProps) {
  const { open, onSubmit, planCellCoordinates, focusPlanCell } = props;
  const mealInd = planCellCoordinates.mealInd;
  const { activeGroupId } = useCurrentUser();

  const persons = usePersonsAsync(false);
  const getPerson = (id: number | undefined) => findById(persons, id);
  const queryClient = useQueryClient();
  const { submitPlanCells, onConsume, getTableDataForCoordinates } =
    useContext(MealPlannerContext);

  const planCells = useMemo(
    () =>
      focusPlanCell
        ? [focusPlanCell]
        : getTableDataForCoordinates({
            ...planCellCoordinates,
            index: -1,
          }).filter(canBeConsumed),
    [focusPlanCell, planCellCoordinates, getTableDataForCoordinates],
  );
  const [edits, setEdits] = useImmer<Record<string, PlannedEdit>>({});
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [picker, setPicker] = useState<{ sig: string; key: string } | null>(
    null,
  );
  const [additional, updateAdditional] = useImmer<JustIntake[]>([]);
  const [consumed, updateConsumed] = useImmer<ConsumedCell[]>([]);
  const [consumedDirty, setConsumedDirty] = useState(false);
  const [loadedDishes, setLoadedDishes] = useState<
    Record<number, DishModification>
  >({});
  // Lade-Gate: Verzehr-Body erst rendern, wenn Rezept + Meal-Foods/Purchases
  // verbindlich geladen sind (sonst leere kcal/fehlende Alternativen – Race).
  const [dishesReady, setDishesReady] = useState(false);
  const [lookupsReady, setLookupsReady] = useState(false);

  const dishIdsKey = useMemo(
    () =>
      [
        ...new Set(
          planCells
            .map((cell) => cell.planCell)
            .filter(
              (planCell): planCell is MealWithIntakes =>
                planCell.indicator === "MealWithIntakes",
            )
            .map((meal) => meal.meal.dishId)
            .filter((id): id is number => id != null && id !== -1),
        ),
      ].join(","),
    [planCells],
  );

  useEffect(() => {
    if (!open) {
      setLoadedDishes({});
      setDishesReady(false);
      return;
    }
    const dishIds = dishIdsKey
      ? dishIdsKey.split(",").map((id) => Number(id))
      : [];
    let cancelled = false;
    (async () => {
      const next: Record<number, DishModification> = {};
      let changed = false;
      for (const id of dishIds) {
        try {
          const dish = await loadDishModification(id);
          // Foods/Purchases des Rezepts (inkl. Alternativen/PersonChoices) laden,
          // bevor die Karte gerendert wird.
          const unitIds = collectFoodUnitIds(dish.steps);
          await ensureFoods(queryClient, [], unitIds);
          const foodIds = [
            ...new Set(
              unitIds
                .map((unitId) => getFoodByUnit(queryClient, unitId)?.id)
                .filter((foodId): foodId is number => foodId != null),
            ),
          ];
          await ensurePurchases(queryClient, [], foodIds);
          next[id] = dish;
          changed = true;
        } catch (e) {
          console.error("Error loading dish:", e);
        }
      }
      if (!cancelled) {
        if (changed) setLoadedDishes(next);
        setDishesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dishIdsKey, queryClient]);

  // Meal-FoodUnits + Purchases vor dem Rendern laden (kcal/UnitAmount).
  useEffect(() => {
    if (!open) {
      setLookupsReady(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mealFoodUnitIds = [
          ...new Set(
            planCells.flatMap(({ planCell }) =>
              planCell.indicator === "MealWithIntakes"
                ? planCell.meal.mealIngredients
                    .map((mi) => mi.foodUnitId)
                    .filter((id): id is number => id != null && id > 0)
                : [],
            ),
          ),
        ];
        await ensureFoods(queryClient, [], mealFoodUnitIds);
        const mealFoodIds = [
          ...new Set(
            mealFoodUnitIds
              .map((unitId) => getFoodByUnit(queryClient, unitId)?.id)
              .filter((id): id is number => id != null),
          ),
        ];
        const purchaseIds = [
          ...new Set(
            planCells.flatMap(({ planCell }) =>
              planCell.indicator === "MealWithIntakes"
                ? planCell.meal.mealIngredients
                    .map((mi) => mi.purchaseUnitPurchaseId)
                    .filter((id): id is number => id != null && id > 0)
                : [],
            ),
          ),
        ];
        await ensurePurchases(queryClient, purchaseIds, mealFoodIds);
      } catch (e) {
        console.error("Verzehr-Lookups unvollständig", e);
      }
      if (!cancelled) setLookupsReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, planCells, queryClient]);

  useEffect(() => {
    if (!open) {
      setEdits({});
      setExpandedCards(new Set());
      setPicker(null);
      updateAdditional([]);
      updateConsumed([]);
      setConsumedDirty(false);
    }
  }, [open, setEdits, updateAdditional, updateConsumed]);

  // Verzehrte Einträge als editierbare Arbeitskopien laden. Im fokussierten
  // Modus wird nur die gewählte PlanCell als Verzehr-Editor bereitgestellt.
  useEffect(() => {
    if (!open) return;
    if (focusPlanCell) {
      const planCell = focusPlanCell.planCell;
      const list: ConsumedCell[] =
        !isDeleted(planCell) && hasActiveConsumption(planCell)
          ? [
              {
                sig: consumedSig(planCell),
                personId: consumedPersonId(planCell),
                index: focusPlanCell.index,
                cell: cloneCell(planCell),
              },
            ]
          : [];
      updateConsumed(list);
      return;
    }
    const coords = planCellCoordinates;
    if (!coords) return;
    const cells = getTableDataForCoordinates({ ...coords, index: -1 });
    const list: ConsumedCell[] = cells
      .filter(
        ({ planCell }) =>
          !isDeleted(planCell) && hasActiveConsumption(planCell),
      )
      .filter(
        ({ planCell }) =>
          !coords.personId || consumedPersonId(planCell) === coords.personId,
      )
      .map(({ planCell, index }) => {
        const personId = consumedPersonId(planCell);
        return {
          sig: consumedSig(planCell),
          personId,
          personName: !coords.personId ? getPerson(personId)?.name : undefined,
          index,
          cell: cloneCell(planCell),
        };
      });
    updateConsumed(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mealIndDescription = mealInds[mealInd]?.name || "";

  const plannedBase = useMemo<PlannedBase[]>(() => {
    if (!planCellCoordinates) return [];
    const out: PlannedBase[] = [];
    for (const { planCell } of planCells) {
      if (planCell.indicator !== "MealWithIntakes") continue;
      if (isDeleted(planCell)) continue;
      if (planCell.intakes != undefined) continue;
      if (
        planCellCoordinates.personId &&
        planCell.meal.personId !== planCellCoordinates.personId
      )
        continue;
      out.push({
        sig: `${planCell.meal.personId}:${planCell.meal.id}`,
        personId: planCell.meal.personId,
        personName: !planCellCoordinates.personId
          ? getPerson(planCell.meal.personId)?.name
          : undefined,
        meal: planCell.meal,
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCellCoordinates, planCells, persons]);

  const plannedCount = plannedBase.filter((b) => edits[b.sig]?.checked).length;
  const canSave = plannedCount > 0 || additional.length > 0 || consumedDirty;
  // Fokussierte Variante zum Bearbeiten eines bereits erfassten Verzehrs.
  const editingConsumption = !!focusPlanCell && consumed.length > 0;

  const hasCustomEdits = useMemo(() => {
    for (const base of plannedBase) {
      const edit = edits[base.sig];
      if (!edit) continue;
      for (const mi of base.meal.mealIngredients) {
        if (
          isIngredientCustom(
            edit.ingredients[String(mi.id)],
            planMetaFromIngredient(mi),
          )
        )
          return true;
      }
    }
    return false;
  }, [plannedBase, edits]);

  const setChecked = (sig: string, checked: boolean) =>
    setEdits((draft) => {
      const existing = draft[sig];
      draft[sig] = {
        checked,
        ingredients: existing?.ingredients ?? {},
      };
    });

  const applyIngredientEdit = (
    sig: string,
    key: string,
    partial: IngredientEdit,
  ) =>
    setEdits((draft) => {
      const existing = draft[sig];
      const ingredients = { ...(existing?.ingredients ?? {}) };
      const merged: IngredientEdit = {
        ...(ingredients[key] ?? {}),
        ...partial,
      };
      if (
        merged.amount === undefined &&
        merged.unitId === undefined &&
        merged.purchaseId === undefined &&
        !merged.deleted
      ) {
        delete ingredients[key];
      } else {
        ingredients[key] = merged;
      }
      draft[sig] = { checked: true, ingredients };
    });

  const resetToPlanned = () =>
    setEdits((draft) => {
      plannedBase.forEach((b) => {
        const existing = draft[b.sig];
        if (!existing) return;
        draft[b.sig] = { checked: existing.checked, ingredients: {} };
      });
    });

  const deletePlanned = (sig: string) => {
    const target = planCells.find(
      ({ planCell }) =>
        planCell.indicator === "MealWithIntakes" &&
        `${planCell.meal.personId}:${planCell.meal.id}` === sig,
    );
    if (!target) return;
    submitPlanCells([
      { planCell: { ...target.planCell, deleted: true }, index: target.index },
    ]);
  };

  const toggleExpanded = (sig: string) =>
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(sig)) next.delete(sig);
      else next.add(sig);
      return next;
    });

  const checkAll = () =>
    setEdits((draft) => {
      plannedBase.forEach((b) => {
        const existing = draft[b.sig] ?? {
          checked: false,
          ingredients: {},
        };
        draft[b.sig] = { ...existing, checked: true };
      });
    });

  const onAddMealSource = async (mealSource: MealSourceIdDto) => {
    if (!planCellCoordinates.personId) return;
    if (mealSource.indicator === "DISH") {
      // Gericht als Meal + Intakes anlegen statt die Zutaten einzeln als
      // JustIntakes in den Plan zu schreiben (Speichern via `mealsWithIntakes`).
      const [cells, intakeResponse] = await Promise.all([
        createPlanCells({
          planCellCoordinates,
          mealSourceId: mealSource,
          groupId: activeGroupId ?? undefined,
        }),
        createIntakesFromEntity({
          body: {
            planCellCoordinates,
            mealSourceId: mealSource,
            groupId: activeGroupId ?? undefined,
          },
        }),
      ]);
      const intakes = intakeResponse.data;
      updateConsumed((draft) => {
        cells.forEach((cell, idx) => {
          if (cell.indicator !== "MealWithIntakes") return;
          draft.push({
            sig: `${cell.meal.personId}:new-${Date.now()}-${idx}`,
            personId: cell.meal.personId,
            index: -1,
            cell: { ...cell, intakes: intakes.map((i) => ({ ...i })) },
          });
        });
      });
      setConsumedDirty(true);
      return;
    }
    const response = await createIntakesFromEntity({
      body: {
        planCellCoordinates,
        mealSourceId: mealSource,
        groupId: activeGroupId ?? undefined,
      },
    });
    updateAdditional((draft) => {
      response.data.forEach((i) => {
        draft.push({ indicator: "JustIntake", ...i } as JustIntake);
      });
    });
  };

  const removeAdditional = (index: number) =>
    updateAdditional((draft) => {
      draft.splice(index, 1);
    });

  const updateAdditionalRow = (index: number, patch: (i: JustIntake) => void) =>
    updateAdditional((draft) => {
      patch(draft[index]);
    });

  const patchConsumedIntake = (
    sig: string,
    intakeIdx: number,
    mutate: (i: IntakeWithEditedFlags) => void,
  ) => {
    updateConsumed((draft) => {
      const c = draft.find((x) => x.sig === sig);
      if (!c) return;
      if (c.cell.indicator === "MealWithIntakes") {
        const intake = c.cell.intakes?.[intakeIdx];
        if (!intake) return;
        mutate(intake);
        intake.edited = true;
      } else {
        mutate(c.cell);
        c.cell.edited = true;
      }
    });
    setConsumedDirty(true);
  };

  const consumedRemove = (sig: string, index: number) =>
    patchConsumedIntake(sig, index, (i) => {
      i.deleted = true;
    });

  const handleSave = () => {
    for (const base of plannedBase) {
      const edit = edits[base.sig];
      if (!edit?.checked) continue;
      onConsume(
        base.meal.mealDate,
        mealInd,
        getMealKeyForMeal(base.meal),
        base.personId,
        buildConsumeOverrides(base.meal, edit, base.personId),
      );
    }
    if (additional.length > 0) {
      submitPlanCells(additional.map((pc) => ({ planCell: pc, index: -1 })));
    }
    if (consumedDirty) {
      submitPlanCells(
        consumed.map((c) => ({ planCell: c.cell, index: c.index })),
      );
    }
    onSubmit();
  };

  const dishOutdated = planCells.some(({ planCell }) => {
    if (planCell.indicator !== "MealWithIntakes") {
      return false;
    }
    const dishId = planCell.meal.dishId;
    if (!dishId || dishId <= 0) {
      return false;
    }
    const currentDishVersion = loadedDishes[dishId]?.version;
    return (
      currentDishVersion != null &&
      planCell.meal.dishVersion != null &&
      currentDishVersion > planCell.meal.dishVersion
    );
  });

  return {
    focusPlanCell,
    planCellCoordinates,
    plannedBase,
    edits,
    expandedCards,
    additional,
    consumed,
    canSave,
    editingConsumption,
    hasCustomEdits,
    picker,
    setPicker,
    mealIndDescription,
    loadedDishes,
    dishOutdated,
    initialLoading: !dishesReady || !lookupsReady,
    setChecked,
    applyIngredientEdit,
    resetToPlanned,
    deletePlanned,
    toggleExpanded,
    checkAll,
    onAddMealSource,
    removeAdditional,
    updateAdditionalRow,
    patchConsumedIntake,
    consumedRemove,
    handleSave,
  };
}

type IntakeState = ReturnType<typeof useIntakeState>;

/**
 * Hook-freie Shell: rendert Header, Body und Footer als eigene Slots. Der
 * Zustand liegt oberhalb der Slot-Boundaries (`useIntakeState`).
 */
export function IntakeDrawer(props: IntakeDrawerProps) {
  const state = useIntakeState(props);
  const entityEdit = useEntityEditDrawers();

  const onEditPlanned = (sig: string) => {
    const meal = state.plannedBase.find((b) => b.sig === sig)?.meal;
    if (!meal) return;
    if (!isNullIndex(meal.dishId)) {
      entityEdit.open({ kind: "dish", id: meal.dishId });
    } else if (!isNullIndex(meal.foodId)) {
      entityEdit.open({ kind: "food", id: meal.foodId });
    } else if (!isNullIndex(meal.purchaseId)) {
      entityEdit.open({ kind: "purchase", id: meal.purchaseId });
    }
  };

  const intakeDishOutdated = state.dishOutdated;

  return (
    <>
      <Plan2PlateDrawer
        open={props.open}
        onClose={props.onClose}
        noScroll
        header={
          <IntakeDrawerHeader
            planCellCoordinates={props.planCellCoordinates}
            focusPlanCell={props.focusPlanCell}
          />
        }
        body={
          state.initialLoading ? (
            <div className="flex w-full flex-col gap-3 px-1 py-2">
              <LookupSkeleton dataCy="intake-loading" lines={5} />
            </div>
          ) : (
            <div className="flex w-full flex-col gap-3">
              <DishVersionWarning outdated={intakeDishOutdated} />
              <IntakeDrawerBody state={state} onEditPlanned={onEditPlanned} />
            </div>
          )
        }
        footer={
          state.canSave ? <IntakeDrawerFooter state={state} /> : undefined
        }
      />
      {entityEdit.element}
    </>
  );
}

function useFocusedMealNutri(focusPlanCell: PlanCellWithIndex | undefined) {
  const mealWithIntakes =
    focusPlanCell?.planCell.indicator === "MealWithIntakes"
      ? (focusPlanCell.planCell as MealWithIntakes)
      : undefined;
  const meals = useMemo(
    () => (mealWithIntakes ? [mealWithIntakes] : []),
    [mealWithIntakes],
  );
  const purchases = useMealPurchases(meals);
  const foodIds = useMemo(
    () => collectFoodIds(meals, purchases),
    [meals, purchases],
  );
  const foods = useFoodsByIds(foodIds);
  if (!mealWithIntakes) return undefined;
  const vals = mapPlanCellToNutriValues(mealWithIntakes, foods, purchases);
  return vals.length > 0 ? vals.reduce(nutriValueSumReducer) : emptyNutriValue;
}

function IntakeDrawerHeader(props: {
  planCellCoordinates: PlanCellCoordinatesWithOptionalPerson;
  focusPlanCell?: PlanCellWithIndex;
}) {
  const cell = props.focusPlanCell?.planCell;
  const mealNutri = useFocusedMealNutri(props.focusPlanCell);
  const justIntake = cell?.indicator === "JustIntake" ? cell : undefined;
  const purchase = usePurchase(justIntake?.purchaseUnitPurchaseId);
  const consumedIntakes = useMemo(
    () => (justIntake ? [justIntake] : []),
    [justIntake],
  );
  const consumedNutri = useConsumedNutri(consumedIntakes);

  if (cell) {
    const description =
      cell.indicator === "MealWithIntakes"
        ? cell.meal.dishDescription ||
          cell.meal.foodDescription ||
          cell.meal.purchaseDescription ||
          ""
        : formatPurchaseDescription(purchase) ||
          cell.purchaseUnitPurchaseDescription;
    const nutri = mealNutri ?? consumedNutri;
    return (
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-bold text-ink">
          {description}
        </div>
        <div className="mt-0.5 text-xs text-ink-muted">
          {Math.round(nutri?.kcal ?? 0)} kcal
        </div>
      </div>
    );
  }

  return (
    <PlanCoordinateHeader
      planMode="intake"
      planCellCoordinates={props.planCellCoordinates}
    />
  );
}

function IntakeDrawerBody({
  state,
  onEditPlanned,
}: {
  state: IntakeState;
  onEditPlanned: (sig: string) => void;
}) {
  const {
    focusPlanCell,
    plannedBase,
    edits,
    expandedCards,
    additional,
    consumed,
    hasCustomEdits,
    picker,
    setPicker,
    mealIndDescription,
    loadedDishes,
    setChecked,
    applyIngredientEdit,
    resetToPlanned,
    deletePlanned,
    toggleExpanded,
    checkAll,
    onAddMealSource,
    removeAdditional,
    updateAdditionalRow,
    patchConsumedIntake,
    consumedRemove,
  } = state;

  const pickerElement = picker
    ? (() => {
        const base = plannedBase.find((b) => b.sig === picker.sig);
        const mi = base?.meal.mealIngredients.find(
          (m) => String(m.id) === picker.key,
        );
        // Produktwahl für eine geplante Zutat (per-Entity-Lookup im Picker)
        if (base && mi) {
          return (
            <PlannedProductPicker
              mi={mi}
              edit={edits[base.sig]?.ingredients[picker.key]}
              onApply={(partial) =>
                applyIngredientEdit(picker.sig, picker.key, partial)
              }
              onClose={() => setPicker(null)}
            />
          );
        }
        // Produktwahl für einen bereits verzehrten Eintrag (Intake-Zeile)
        const cCell = consumed.find((x) => x.sig === picker.sig);
        const intake =
          cCell?.cell.indicator === "MealWithIntakes"
            ? cCell.cell.intakes?.[Number(picker.key)]
            : cCell?.cell;
        if (!cCell || !intake) return null;
        return (
          <ConsumedProductPicker
            intake={intake}
            onApply={(selection) =>
              patchConsumedIntake(picker.sig, Number(picker.key), (i) => {
                i.amount = selection.amount;
                i.purchaseUnitPurchaseId = selection.purchaseId;
                i.purchaseUnitPurchaseDescription =
                  selection.purchaseDescription;
                i.purchaseUnitId = selection.purchaseUnitId;
                i.purchaseUnitDescription = selection.purchaseUnitDescription;
              })
            }
            onClose={() => setPicker(null)}
          />
        );
      })()
    : null;

  return (
    <>
      <div
        id="intake-drawer-scroll"
        className="min-h-0 w-full flex-1 overflow-y-auto"
      >
        <div className="flex flex-col gap-4">
          <IntakePlannedSection
            plannedBase={plannedBase}
            edits={edits}
            expandedCards={expandedCards}
            loadedDishes={loadedDishes}
            hasCustomEdits={hasCustomEdits}
            onToggleChecked={setChecked}
            onToggleExpand={toggleExpanded}
            onOpenPicker={(sig, key) => setPicker({ sig, key })}
            onApplyEdit={applyIngredientEdit}
            onEdit={onEditPlanned}
            onDelete={deletePlanned}
            onReset={resetToPlanned}
            onCheckAll={checkAll}
          />

          <IntakeConsumedSection
            consumed={consumed}
            expandedCards={expandedCards}
            onToggleExpand={toggleExpanded}
            onPatchIntake={patchConsumedIntake}
            onOpenPicker={(sig, index) =>
              setPicker({ sig, key: String(index) })
            }
            onRemoveIntake={consumedRemove}
          />

          {!focusPlanCell && (
            <AdditionalIntakeSection
              mealIndDescription={mealIndDescription}
              justIntakes={additional}
              onAddMealSource={onAddMealSource}
              onRemove={removeAdditional}
              onUpdate={updateAdditionalRow}
            />
          )}
        </div>
      </div>
      {pickerElement}
    </>
  );
}

function IntakeDrawerFooter({ state }: { state: IntakeState }) {
  return (
    <Button
      dataCy={
        state.editingConsumption
          ? "verzehr-bearbeiten-save-button"
          : "verzehr-save-button"
      }
      fullWidth
      className="rounded-card bg-teal py-3.5 font-semibold text-white"
      onClick={state.handleSave}
    >
      ✓ Verzehr speichern
    </Button>
  );
}
